// WbVoidTicketModal — Phase C11. Wires the "Void ticket" action into the
// detail page's Actions card. POSTs to /wb/tickets/:enc_id/void with a
// required void_reason (3–2000 chars). On success the modal closes, a
// toast fires, and the ticket query is invalidated so the page rerenders
// with status='voided' (which surfaces the existing Void info card and
// the Reissue affordance).
//
// Any barrier_actions[] returned in the response are logged to the
// console — the WB module never dispatches barriers over HTTP (the
// on-prem agent owns the hardware). See WEIGHBRIDGE_SERVER_CONTRACT.md
// §C10 / §C11.

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import { extractErrorMessage } from '../../../utils/errorMessage';
import { useToastStore } from '../../../store/toastStore';
import { Button } from '../../../components/ui/Button/Button';
import { Modal } from '../../../components/ui/Modal/Modal';
import type { TicketDetailDto } from '../hooks/useWbTicket';

interface VoidTicketBody {
  void_reason: string;
}

interface VoidTicketResponse {
  ticket: TicketDetailDto;
  /** Optional — present when the void event has barrier trigger rules. */
  barrier_actions?: ReadonlyArray<{
    barrier_enc_id: string;
    target_state: string;
    hold_ms: number;
  }>;
}

const MIN_REASON_LEN = 3;
const MAX_REASON_LEN = 2000;

export interface WbVoidTicketModalProps {
  open: boolean;
  ticketEncId: string;
  onClose: () => void;
}

export function WbVoidTicketModal({
  open,
  ticketEncId,
  onClose,
}: WbVoidTicketModalProps): JSX.Element | null {
  const [voidReason, setVoidReason] = useState('');
  const [inlineError, setInlineError] = useState<string | null>(null);
  const qc = useQueryClient();
  const pushToast = useToastStore((s) => s.push);

  // Reset form whenever the modal toggles open. We don't reset on close
  // because the user may have closed accidentally — let them re-open and
  // see their unsent text. The lifecycle here mirrors the C10 complete
  // modal.
  useEffect(() => {
    if (open) {
      setVoidReason('');
      setInlineError(null);
    }
  }, [open]);

  const mutation = useMutation<VoidTicketResponse, Error, VoidTicketBody>({
    mutationFn: async (body) => {
      const res = await apiClient.post<VoidTicketResponse, VoidTicketBody>(
        endpoints.wb.ticketVoid(ticketEncId),
        body,
      );
      return res.data;
    },
    onSuccess: (data) => {
      if (data.barrier_actions && data.barrier_actions.length > 0) {
        // eslint-disable-next-line no-console
        console.log('Barrier actions:', data.barrier_actions);
      }
      pushToast({ type: 'success', message: 'Ticket voided.' });
      void qc.invalidateQueries({ queryKey: queryKeys.wb.ticket(ticketEncId) });
      void qc.invalidateQueries({ queryKey: ['wb', 'tickets'] });
      onClose();
    },
    onError: (err) => {
      setInlineError(extractErrorMessage(err) ?? 'Failed to void ticket.');
    },
  });

  const trimmed = voidReason.trim();
  const canSubmit =
    trimmed.length >= MIN_REASON_LEN &&
    trimmed.length <= MAX_REASON_LEN &&
    !mutation.isPending;

  function handleSubmit() {
    setInlineError(null);
    if (!canSubmit) return;
    mutation.mutate({ void_reason: trimmed });
  }

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Void ticket"
      size="md"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={mutation.isPending}
            data-testid="void-confirm-button"
          >
            Void ticket
          </Button>
        </>
      }
    >
      <p>
        This will be recorded on the ticket and cannot be undone. Stock
        movements from this ticket are reversed automatically.
      </p>
      {inlineError !== null ? (
        <p role="alert" data-testid="void-inline-error">
          {inlineError}
        </p>
      ) : null}
      <label>
        <span>Reason for voiding</span>
        <textarea
          value={voidReason}
          onChange={(e) => setVoidReason(e.target.value)}
          minLength={MIN_REASON_LEN}
          maxLength={MAX_REASON_LEN}
          rows={4}
          required
          aria-label="Reason for voiding"
          data-testid="void-reason-textarea"
        />
        <small>
          {trimmed.length < MIN_REASON_LEN
            ? `${MIN_REASON_LEN - trimmed.length} more character(s) needed.`
            : `${trimmed.length} / ${MAX_REASON_LEN}`}
        </small>
      </label>
    </Modal>
  );
}
